import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FilterPreview,
  FilterRecord,
  FilterService,
  Operator,
} from '@services/core-services/filters';
import { FilterOperatorLabelComponent } from '@enginuity/core/organisms/filtering/filter-operator-label/filter-operator-label.component';
import { CeIconComponent } from '@enginuity/core/atoms/ce-icon/ce-icon.component';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-filter-preview-default',
  templateUrl: './filter-preview-default.component.html',
  styleUrl: './filter-preview-default.component.scss',
  imports: [CommonModule, FilterOperatorLabelComponent, CeIconComponent],
})
export class FilterPreviewDefaultComponent implements FilterPreview {
  @Input() record!: FilterRecord;
  @Input() readonly: boolean = false;
  protected readonly Operator = Operator;

  @Output() onRemove: EventEmitter<FilterRecord> = new EventEmitter<FilterRecord>();

  get requiresValue(): boolean {
    return this.filter.requiresValue(this.record);
  }

  get requiresValueDisplay(): boolean {
    return this.filter.requiresValueDisplay(this.record);
  }

  get requiresLastValueDisplay(): boolean {
    return this.filter.requiresLastValueDisplay(this.record);
  }

  constructor(private readonly filter: FilterService) {}

  onRecordRemove() {
    this.onRemove.emit(this.record);
  }

  get formattedDisplayValue() {
    const value = this.record?.rule?.value;
    const operator = this.record?.rule?.operator;

    if (operator === Operator.LastX) {
      let days: number | undefined;
      if (typeof value === 'number') {
        days = value;
      } else if (typeof value === 'string') {
        // Attempt to parse if it's a string representation
        const parsed = parseInt(value, 10);
        if (!isNaN(parsed)) {
          days = parsed;
        }
      }

      if (days !== undefined && days > 0) {
        return `${days} day${days !== 1 ? 's' : ''}`;
      }
      return '';
    }

    if (!value) return '';

    const format = 'dd MMM yy';

    if (Array.isArray(value)) {
      try {
        const start = DateTime.fromISO(value[0]);
        const end = DateTime.fromISO(value[1]);
        if (start.isValid && end.isValid) {
          if (start.hasSame(end, 'day')) {
            return start.toFormat(format);
          }
          return `${start.toFormat(format)} - ${end.toFormat(format)}`;
        }
      } catch (e) {
        /* Ignore error, fallback below */
      }
      return value.join(', ');
    }

    try {
      const date = DateTime.fromISO(String(value));
      if (date.isValid) {
        return date.toFormat(format);
      }
    } catch (e) {
      /* Ignore error, fallback below */
    }

    return String(value);
  }
}
